import React from 'react';

export function Card({ children, className = '' }) {
  return (
    <div className={`rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

export function NumberField({ label, value, onChange, prefix = '', suffix = '', step = '1', className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-medium text-slate-600">{label}</span>
      <div className="flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-blue-200">
        {prefix && <span className="mr-1.5 shrink-0 text-slate-500 text-sm">{prefix}</span>}
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="min-w-0 w-full bg-transparent text-sm font-semibold outline-none"
        />
        {suffix && <span className="ml-1.5 shrink-0 text-slate-500 text-xs whitespace-nowrap">{suffix}</span>}
      </div>
    </label>
  );
}

export function TextField({ label, value, onChange, placeholder = '' }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-600">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-base font-semibold outline-none focus:ring-2 focus:ring-blue-200"
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-medium text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-200"
      >
        {options.map(([v, lbl]) => (
          <option key={v} value={v}>{lbl}</option>
        ))}
      </select>
    </label>
  );
}

export function Metric({ label, value, tone, strong }) {
  const toneClass = tone === 'green' ? 'text-emerald-700' : tone === 'red' ? 'text-red-600' : tone === 'orange' ? 'text-orange-600' : 'text-slate-900';
  return (
    <div className={`rounded-xl border border-slate-100 bg-slate-50 p-4 ${strong ? 'ring-2 ring-slate-900' : ''}`}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

export function ComparisonBlock({ rows }) {
  return (
    <div className="space-y-2">
      {rows.map(([label, value, tone]) => (
        <div key={label} className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5">
          <span className="text-sm text-slate-600">{label}</span>
          <span className={`text-right font-bold ${tone === 'green' ? 'text-emerald-700' : tone === 'red' ? 'text-red-600' : 'text-slate-900'}`}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Rule({ ok, label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
      <span className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

export function StatusBox({ ok, title, text }) {
  return (
    <div className={`mt-4 rounded-xl p-4 ${ok ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
      <p className="font-bold">{title}</p>
      <p className="mt-1 text-sm">{text}</p>
    </div>
  );
}

export function AlertBanner({ children, tone = 'amber' }) {
  const cls = tone === 'red' ? 'bg-red-50 text-red-900 border-red-200' : 'bg-amber-50 text-amber-900 border-amber-200';
  return (
    <div className={`rounded-xl border p-4 text-sm ${cls}`}>{children}</div>
  );
}

export function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${active ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}
    >
      {children}
    </button>
  );
}

export function ModalSimple({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            Cerrar
          </button>
        </div>
        <div className="max-h-[calc(85vh-4rem)] overflow-auto p-6">{children}</div>
      </div>
    </div>
  );
}

export function ReadonlyField({ label, value, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-medium text-slate-600">{label}</span>
      <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
        {value}
      </div>
    </label>
  );
}
